/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStandortgruppe } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useStandortgruppen/useStandortgruppen';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { createContext, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { View } from '@/domain/mitarbeiter/features/rotationsplan/entities/views';
import {
  createRotationsplanFeatureStore,
  RotationsplanFeatureStore,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/store';
import { useRotationsplaene } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplaene/useRotationsplaene';
import { RotationsplanFeatureProvider } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { useBerufe } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useBerufe/useBerufe';
import { Page } from '@/domain/mitarbeiter/pages/rotationsplan/overview/Page';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { NavigateToV1 } from '@/domain/mitarbeiter/pages/rotationsplan/NavigateToV1';

// eslint-disable-next-line react-refresh/only-export-components
export const StoreContext = createContext<RotationsplanFeatureStore | null>(
  null,
);

function validateRouteParam(view: string): boolean {
  const validViews: View[] = ['planung', 'uebersicht'];
  return validViews.includes(view as View);
}

export function PageWrapper() {
  const { t } = useTranslation();
  const { currentStandort, standorte } = useStandortContext();
  const standortIds = standorte.map((standort) => standort.id);
  const { standortGruppe } = useStandortgruppe(
    currentStandort?.idStandortgruppe,
  );
  const { rotationsplaene } = useRotationsplaene(standortIds);
  const { berufe } = useBerufe(standortGruppe.id);
  const { view } = useParams();
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year');
  const navigate = useNavigate();

  const currentDefaultStandort = useMemo(
    () =>
      new Standort({
        id: currentStandort.uid,
        name: currentStandort.name,
        storenummer: currentStandort.storeNummer,
      }),
    [currentStandort.name, currentStandort.storeNummer, currentStandort.uid],
  );

  const store = useMemo(
    () =>
      createRotationsplanFeatureStore(
        rotationsplaene,
        berufe,
        standortGruppe.standorte,
        currentDefaultStandort,
        year ?? dayjs(new Date()).get('year').toString(),
      ),
    [
      berufe,
      currentDefaultStandort,
      rotationsplaene,
      standortGruppe.standorte,
      year,
    ],
  );

  useEffect(() => {
    if ((!view || !validateRouteParam(view)) && !year) {
      const currentYear = dayjs(new Date()).get('year');
      navigate(`/mitarbeiter/rotationsplaene/planung?year=${currentYear}`, {
        replace: true,
      });
    } else if (!view || !validateRouteParam(view)) {
      navigate(`/mitarbeiter/rotationsplaene/planung?year=${year}`, {
        replace: true,
      });
    } else if (!year && view === 'planung') {
      const currentYear = dayjs(new Date()).get('year');
      navigate(`/mitarbeiter/rotationsplaene/${view}?year=${currentYear}`, {
        replace: true,
      });
    }
  }, [navigate, view, year]);

  if (!standortGruppe || !standortGruppe.istRotationsplanAktiviert) {
    return <NavigateToV1 />;
  }

  return (
    <>
      <Helmet>
        <title>{t('domain.mitarbeiter.rotationsplan.title')}</title>
      </Helmet>
      <StoreContext.Provider value={store}>
        <RotationsplanFeatureProvider>
          <Page view={view as View} year={year} />
        </RotationsplanFeatureProvider>
      </StoreContext.Provider>
    </>
  );
}
